import React, { useState, useMemo, useCallback } from "react";
import { Heading } from "../../design/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import { Text } from "../../design/text";
import { useReport } from "../../contexts/ReportContext";
import moment from "moment";
import ReportStatusBadge from "../../components/ReportStatusBadge";
import { useCase } from "../../contexts/CaseContext";
import { useSupervisor } from "../../contexts/SupervisorContext";
import LoadingView from "../../components/LoadingView";
import ErrorView from "../../components/ErrorView";
import ReportFilters from "../ReportsPage/components/ReportFilters";
import EmptyStateView from "../../components/EmptyStateView";

type FilterStatus = "all" | "pending" | "approved" | "completed";

/**
 * Admin reports page - shows all reports with full management capabilities
 */
const AdminReports: React.FC = () => {
  const { reports, status, setStatus, loading, error } = useReport();
  const { cases } = useCase();
  const { supervisors } = useSupervisor();

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");

  // Create lookup maps for efficient filtering
  const supervisorMap = useMemo(() => {
    const map = new Map<string, { firstName: string; lastName: string }>();
    supervisors.forEach((supervisor) => {
      map.set(supervisor.id, {
        firstName: supervisor.firstName,
        lastName: supervisor.lastName,
      });
    });
    return map;
  }, [supervisors]);

  const caseMap = useMemo(() => {
    const map = new Map<string, string>();
    cases.forEach((caseItem) => {
      map.set(caseItem.id, caseItem.identifier || "");
    });
    return map;
  }, [cases]);

  // Memoized filtered reports
  const filteredReports = useMemo(() => {
    const searchLower = searchTerm.trim().toLowerCase();
    const hasSearch = searchLower.length > 0;
    const hasStartDate = startDateFilter.length > 0;
    const hasEndDate = endDateFilter.length > 0;

    // Parse date filters once
    const startDate = hasStartDate ? moment(startDateFilter).startOf("day") : null;
    const endDate = hasEndDate ? moment(endDateFilter).endOf("day") : null;

    return reports.filter((report) => {
      // Search filter - check supervisor name and case identifier
      if (hasSearch) {
        const supervisor = supervisorMap.get(report.supervisorId);
        const supervisorName = supervisor
          ? `${supervisor.firstName} ${supervisor.lastName}`.toLowerCase()
          : "";
        const caseIdentifier = (caseMap.get(report.caseId) || "").toLowerCase();

        if (!supervisorName.includes(searchLower) && !caseIdentifier.includes(searchLower)) {
          return false;
        }
      }

      // Date range filter - only check if visit exists
      if (report.visit?.startDate) {
        const reportStartDate = moment(report.visit.startDate.toDate());

        if (startDate && reportStartDate.isBefore(startDate, "day")) {
          return false;
        }

        if (endDate && reportStartDate.isAfter(endDate, "day")) {
          return false;
        }
      } else if (hasStartDate || hasEndDate) {
        // If date filters are set but report has no visit, exclude it
        return false;
      }

      return true;
    });
  }, [reports, searchTerm, startDateFilter, endDateFilter, supervisorMap, caseMap]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(e.target.value as FilterStatus);
  }, [setStatus]);

  const handleStartDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDateFilter(e.target.value);
  }, []);

  const handleEndDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDateFilter(e.target.value);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setStartDateFilter("");
    setEndDateFilter("");
    setStatus("all");
  }, [setStatus]);

  const hasActiveFilters = Boolean(
    searchTerm.trim() ||
    startDateFilter ||
    endDateFilter ||
    (status && status !== "all")
  );

  const hasReports = reports.length > 0;
  const hasFilteredReports = filteredReports.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Heading>Reports</Heading>
          {hasReports && (
            <Text className="mt-1">
              {filteredReports.length} of {reports.length} reports
              {hasActiveFilters && " (filtered)"}
            </Text>
          )}
        </div>
      </div>

      {/* Filters */}
      {hasReports && (
        <ReportFilters
          searchTerm={searchTerm}
          statusFilter={status}
          startDateFilter={startDateFilter}
          endDateFilter={endDateFilter}
          onSearchChange={handleSearchChange}
          onStatusChange={handleStatusChange}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onClearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
        />
      )}

      {/* Loading and Error States */}
      {loading.list && <LoadingView />}
      {error.list && <ErrorView error={error.list} />}

      {/* Empty States */}
      {!loading.list && !error.list && !hasReports && (
        <EmptyStateView 
          title="No Reports Found" 
          message="Reports are created automatically when visits are completed" 
        />
      )}

      {!loading.list && !error.list && hasReports && !hasFilteredReports && (
        <div className="mt-8 text-center">
          <Heading>No reports match your filters</Heading>
          <Text className="mt-2">
            Try adjusting your search criteria or{" "}
            <button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 underline"
            >
              clear all filters
            </button>
          </Text>
        </div>
      )}

      {/* Reports Table */}
      {!loading.list && !error.list && hasFilteredReports && (
        <Table 
          striped
          className="mt-4 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]"
        >
          <TableHead>
            <TableRow>
              <TableHeader>Monitor</TableHeader>
              <TableHeader>Start Date</TableHeader>
              <TableHeader>End Date</TableHeader>
              <TableHeader>Case</TableHeader>
              <TableHeader>Status</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredReports.map((report) => {
              const supervisor = supervisorMap.get(report.supervisorId);
              const caseIdentifier = caseMap.get(report.caseId);
              
              return (
                <TableRow 
                  key={report.id} 
                  href={`/report/${report.id}`}
                  title={`View report for ${caseIdentifier || "case"}`}
                >
                  <TableCell className="font-medium">
                    {supervisor
                      ? `${supervisor.firstName} ${supervisor.lastName}`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {report.visit?.startDate ? (
                      <span className="whitespace-nowrap">
                        {moment(report.visit.startDate.toDate()).format("MMM D, YYYY")}
                        <span className="text-zinc-500 dark:text-zinc-400 ml-1">
                          {moment(report.visit.startDate.toDate()).format("h:mm A")}
                        </span>
                      </span>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {report.visit?.endDate ? (
                      <span className="whitespace-nowrap">
                        {moment(report.visit.endDate.toDate()).format("MMM D, YYYY")}
                        <span className="text-zinc-500 dark:text-zinc-400 ml-1">
                          {moment(report.visit.endDate.toDate()).format("h:mm A")}
                        </span>
                      </span>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {caseIdentifier || "—"}
                  </TableCell>
                  <TableCell>
                    <ReportStatusBadge status={report.status} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default AdminReports;
